"use client";

import React from "react";
import { Maximize2, Clock, Layers, Wrench, ArrowRight, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
const projects = [{
  id: 1,
  title: "The Ethereal Aerialist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1671966/img/aerialist_performance_stage.png",
  complexity: "Elite Acrobatics",
  material: "Silk & Steel",
  machiningTime: "12 Years Training",
  description: "A breathtaking display of gravity-defying grace. Our lead aerialist combines classical ballet with high-altitude strength, performing on custom-engineered silk apparatuses for global arena tours.",
  ratio: 4 / 3
}, {
  id: 2,
  title: "Neon Pulse Dancers",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1671966/img/neon_dance_troupe.png",
  complexity: "Synchronized Motion",
  material: "LED Fiber Optics",
  machiningTime: "Global Tour Ready",
  description: "A high-energy fusion of street dance and cutting-edge technology. This troupe utilizes programmable LED suits to create a visual spectacle that has headlined festivals from Tokyo to Ibiza.",
  ratio: 1 / 1
}, {
  id: 3,
  title: "The Midnight Virtuoso",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1671966/img/violinist_solo_performance.png",
  complexity: "Classical Fusion",
  material: "Carbon Fiber Violin",
  machiningTime: "Master Class Level",
  description: "Redefining the boundaries of the violin. This virtuoso blends haunting classical melodies with modern electronic soundscapes, perfect for elite gala events and cinematic openings.",
  ratio: 16 / 9
}];
export default function ProjectGallery() {
  return <section data-section-id="236069" id="featured-artists" className="bg-background text-foreground py-20 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 md:mb-20 max-w-3xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-widest mb-4 uppercase">
            Working With <span className="text-primary">Stars</span>
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Witness the elite talent of International Experiences. From world-class musicians to avant-garde circus performers, we curate the most extraordinary acts for the world's most prestigious stages.
          </p>
        </div>

        {}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
          {projects.map((project, i) => <div key={project.id} data-index={i} className="break-inside-avoid">
              <Dialog>
                <DialogTrigger asChild>
                  <div className="group relative w-full block overflow-hidden rounded-md border border-border bg-card text-left transition-all hover:shadow-2xl cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <AspectRatio ratio={project.ratio}>
                      <img src={project.image} alt={project.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-background/80 to-background opacity-0 transition-opacity duration-500 group-hover:opacity-100 flex flex-col items-center justify-center p-6 text-center backdrop-blur-sm">
                        <Maximize2 className="w-8 h-8 text-primary mb-3" />
                        <span className="text-foreground font-default font-bold text-xl tracking-wider uppercase">
                          {project.title}
                        </span>
                        <span className="text-primary text-sm mt-2 font-mono font-medium">
                          VIEW PORTFOLIO
                        </span>
                      </div>
                    </AspectRatio>
                  </div>
                </DialogTrigger>
                <DialogContent className="max-w-4xl p-0 overflow-hidden border-border rounded-md shadow-4xl bg-card">
                  <div className="grid md:grid-cols-2 h-full">
                    {}
                    <div className="relative bg-muted h-64 md:h-auto">
                      <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent md:hidden" />
                    </div>

                    {}
                    <div className="p-6 md:p-8 flex flex-col h-full bg-card">
                      <DialogHeader className="mb-6 text-left">
                        <DialogTitle className="text-2xl md:text-3xl font-bold font-default text-foreground mb-2 tracking-wide uppercase">
                          {project.title}
                        </DialogTitle>
                        <DialogDescription className="text-base text-muted-foreground font-sans leading-relaxed">
                          {project.description}
                        </DialogDescription>
                      </DialogHeader>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 flex-grow">
                        <div className="space-y-1.5">
                          <div className="flex items-center text-xs text-muted-foreground font-sans mb-1 uppercase tracking-widest">
                            <Layers className="w-4 h-4 mr-2 text-primary" />
                            Specialty
                          </div>
                          <div className="font-mono font-medium text-foreground bg-secondary px-3 py-2 rounded-md border border-border">
                            {project.complexity}
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex items-center text-xs text-muted-foreground font-sans mb-1 uppercase tracking-widest">
                            <Wrench className="w-4 h-4 mr-2 text-primary" />
                            Apparatus
                          </div>
                          <div className="font-mono font-medium text-foreground bg-secondary px-3 py-2 rounded-md border border-border">
                            {project.material}
                          </div>
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                          <div className="flex items-center text-xs text-muted-foreground font-sans mb-1 uppercase tracking-widest">
                            <Clock className="w-4 h-4 mr-2 text-primary" />
                            Experience
                          </div>
                          <div className="font-mono font-medium text-foreground bg-secondary px-3 py-2 rounded-md border border-border">
                            {project.machiningTime}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4 mb-8">
                        <h4 className="text-xs font-bold text-primary uppercase tracking-widest font-default">Elite Standards</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start text-sm text-muted-foreground">
                            <CircleCheck className="w-4 h-4 mr-2 text-primary shrink-0 mt-0.5" />
                            <span>Global touring logistics & visa management</span>
                          </li>
                          <li className="flex items-start text-sm text-muted-foreground">
                            <CircleCheck className="w-4 h-4 mr-2 text-primary shrink-0 mt-0.5" />
                            <span>Bespoke performance choreography</span>
                          </li>
                        </ul>
                      </div>

                      <DialogFooter className="mt-auto sm:justify-start">
                        <Button asChild size="lg" className="w-full sm:w-auto group bg-primary text-primary-foreground hover:opacity-90 rounded-md">
                          <Link to="/SecureYourTalentPage">
                            Secure This Talent
                            <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                          </Link>
                        </Button>
                      </DialogFooter>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}